// Архивираните имоти: продадени или с прекратена работа. Историята им се
// пази; оттук могат да се върнат в активните.

import React, { useCallback, useState } from 'react';
import { Image, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL } from '../../api';
import { confirmAction, showError, showSuccess } from '../../toast';
import { Button, Card, COLORS, EmptyState, Muted, Screen, Title } from '../../ui';
import { getLocale, t } from '../../i18n';

type Archived = {
  id: string;
  address: string;
  city: string;
  photos: string[];
  updatedAt: string;
  owner: { firstName: string; lastName: string } | null;
};

export default function ArchivedApartmentsScreen() {
  const [apartments, setApartments] = useState<Archived[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api('/apartments/archived')
      .then((r) => setApartments(r.apartments))
      .catch((e) => showError(e.message));
  }, []);

  useFocusEffect(useCallback(() => load(), [load]));

  const restore = (a: Archived) =>
    confirmAction(
      t('Връщане от архива'),
      t('{address}, {city} става активен имот отново.', { address: a.address, city: a.city }),
      t('Върни'),
      async () => {
        setBusy(true);
        try {
          await api(`/apartments/${a.id}/unarchive`, { body: {} });
          showSuccess(t('Имотът е върнат от архива.'));
          load();
        } catch (e: any) {
          showError(e.message);
        } finally {
          setBusy(false);
        }
      }
    );

  if (apartments === null) {
    return (
      <Screen>
        <Title>{t('Архивирани имоти')}</Title>
        <Muted>{t('Зареждане…')}</Muted>
      </Screen>
    );
  }

  return (
    <Screen>
      <Title>{t('Архивирани имоти')}</Title>
      <Muted>{t('Историята им (плащания, документи, протоколи) е запазена. Не се броят в лимита на плана.')}</Muted>

      {apartments.length === 0 && (
        <EmptyState icon="🗄️" title={t('Архивът е празен')} text={t('Имот се архивира от екрана му: Още → „Архивирай имота“.')} />
      )}

      {apartments.map((a) => (
        <Card key={a.id}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            {a.photos?.[0] ? (
              <Image
                source={{ uri: `${API_URL}${a.photos[0]}` }}
                style={{ width: 56, height: 56, borderRadius: 10, backgroundColor: COLORS.bg }}
              />
            ) : (
              <View
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 10,
                  backgroundColor: COLORS.bg,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text style={{ fontSize: 22 }}>🏠</Text>
              </View>
            )}
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text }}>
                {a.address}, {a.city}
              </Text>
              <Muted>
                {t('архивиран на {date}', { date: new Date(a.updatedAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
                {a.owner ? ` · ${t('собственик {name}', { name: `${a.owner.firstName} ${a.owner.lastName}` })}` : ''}
              </Muted>
            </View>
          </View>
          <Button title={t('Върни от архива')} variant="secondary" onPress={() => restore(a)} disabled={busy} />
        </Card>
      ))}
    </Screen>
  );
}
